import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import styled, { css } from "styled-components";

import { currencyFormatter, handleResponse } from "_helpers";
import { checkIfNumberOfPaymentAttemptsExceeded, increaseBillingCounter, resetBillingCounter } from "_helpers/billing";
import { ordersService } from "_services";
import { ordersActions, customerActions } from "_store/actions";
import { REGISTER_TRADEMARK_CODE_ARRAY, UPSELL_PRODUCT_CODES } from "_constants";

import { SpinnerBlock } from "widget/Spinner";

const Text = styled.div`
  font-family: "Montserrat";

  &:not(:first-child) {
    margin-top: 24px;
  }

  ${({ textCenter }) =>
    textCenter &&
    css`
      width: 100%;
      text-align: center;
    `}

  ${({ textJustify }) =>
    textJustify &&
    css`
      width: 100%;
      text-align: justify;
    `}

  ${({ spaceBetween }) =>
    spaceBetween &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 24px;
      align-items: end;
    `}
`;

const StyledBody = styled(Modal.Body)`
  max-height: 80dvh;
  overflow-y: auto;
`;

const StyledFooter = styled(Modal.Footer)`
  ${({ $spaceBetween }) =>
    $spaceBetween &&
    css`
      justify-content: space-between;
    `}
`;

const ListContainer = styled.div`
  margin-bottom: 24px;
  height: 100%;
  max-height: 100%;
`;

const ProductContainer = styled.div`
  display: flex;
  padding: 12px 4px;
  border-bottom: 1px solid #ecedf1;
  cursor: pointer;

  ${({ index }) =>
    index % 2 !== 0 &&
    css`
      background-color: #fafaff;
    `}

  &:hover {
    background-color: rgba(155, 174, 201, 0.1);
  }

  &:active {
    background-color: rgba(155, 174, 201, 0.25);
  }
`;
const DataCell = styled.div`
  flex: 11;
  &:last-child {
    flex: 1;
  }
`;

const ErrorBox = styled.div`
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 12px;
`;

const EmptyMessage = styled(Text)`
  color: #7b848d;
  font-weight: bold;
  text-align: center;
  font-family: "Montserrat";
`;

const STEPS = {
  init: "step-init",
  select: "step-select",
  start: "step-start",
  loading: "step-loading",
  success: "step-success",
  error: "step-error",
};

const BASIC_PRICE = 59;
const UPSELL_CODE = UPSELL_PRODUCT_CODES.certifiedCopy;

function CertifiedCopyUpsell(props) {
  const dispatch = useDispatch();
  const { onCancel } = props;

  const { ordersList, ordersChildsList, ordersListLoading } = useSelector(({ orders }) => orders);

  const [step, setStep] = useState(STEPS.select);
  const [error, setError] = useState("");
  const [registerDetails, setRegisterDetails] = useState(null);

  const registerProductsList = ordersList.reduce((acc, order) => {
    const registerProduct = order?.products?.find((product) =>
      REGISTER_TRADEMARK_CODE_ARRAY.includes(product?.code?.code)
    );

    const existingProduct = order?.products?.find((product) => product?.code?.code === UPSELL_CODE);
    if (existingProduct) return acc;

    const existingUpsell = ordersChildsList[order?.uid]?.find((upsellOrder) =>
      upsellOrder.productCodes?.find((upsellProduct) => upsellProduct.code === UPSELL_CODE)
    );
    if (existingProduct) return acc;

    if (registerProduct && !existingProduct && !existingUpsell) {
      return [...acc, { ...registerProduct, crmOrderId: order?.crmOrderId }];
    }

    return acc;
  }, []);

  const total = BASIC_PRICE;

  function handleCloseModal({ update = false }) {
    setStep(STEPS.init);
    setError("");
    setRegisterDetails(null);
    onCancel();
    update && dispatch(ordersActions.getOrders());
  }

  async function createUpsell() {
    try {
      const isNumberOfPaymentAttemptsExceeded = checkIfNumberOfPaymentAttemptsExceeded();

      if (isNumberOfPaymentAttemptsExceeded) {
        throw new Error("Uh oh - your card on file has been declined. Please update your payment method to proceed");
      }

      const body = {
        order: { processingOption: "STANDARD" },
        products: [{ code: { code: UPSELL_CODE } }],
      };

      const request = await ordersService.createDelayedUpsell(registerDetails?.orderId, body, true);
      const requestStatus = request?.status;

      handleResponse(request)
        .then((response) => {
          if (requestStatus === 200) {
            if (Array.isArray(response?.products) && response?.products[0]?.paymentStatus === "Declined") {
              increaseBillingCounter();
              setError("Uh oh - your card on file has been declined. Please update your payment method to proceed.");
              setStep(STEPS.error);
            } else {
              resetBillingCounter();
              setStep(STEPS.success);
            }
          } else {
            setError(response?.toString() || "Unknown error");
            setStep(STEPS.error);
          }
        })
        .catch((error) => {
          setError(error?.message || error?.toString() || "Unknown error");
          setStep(STEPS.error);
        });
    } catch (response) {
      setError(response?.message || response?.toString() || "Unknown error");
      setStep(STEPS.error);
    }
  }

  useEffect(() => {
    dispatch(customerActions.getPaymentMethods());
  }, [dispatch]);

  switch (step) {
    case STEPS.select:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text textCenter>
              Which Trademark are you looking
              <br />
              to get a Certified Copy for?
            </Text>
          </Modal.Header>
          <StyledBody>
            <ListContainer>
              {registerProductsList.map((product, idx) => {
                return (
                  <ProductContainer
                    index={idx}
                    key={`regTm-option-${idx}`}
                    title={"#" + product?.crmOrderId}
                    onClick={() => {
                      setRegisterDetails(product);
                      setStep(STEPS.start);
                    }}
                  >
                    <DataCell>
                      <Text>
                        {product?.markDesc}
                        &nbsp;
                        <span style={{ color: "gray" }}>({product?.trademarkType})</span>
                      </Text>
                    </DataCell>
                    <DataCell>
                      <Text>
                        <span style={{ color: "#c0c0c0" }}>
                          {"#"}
                          {idx + 1}
                        </span>
                      </Text>
                    </DataCell>
                  </ProductContainer>
                );
              })}
              {ordersListLoading && <SpinnerBlock />}
              {!ordersListLoading && registerProductsList?.length < 1 && (
                <EmptyMessage>No relevant trademarks were found.</EmptyMessage>
              )}
            </ListContainer>
          </StyledBody>
        </Modal>
      );

    case STEPS.start:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: false })}>
          <Modal.Header closeButton>
            <Text>Certified Copy</Text>
          </Modal.Header>
          <Modal.Body>
            <Text>
              <p>
                Would you like to purchase a Certified Copy of your Trademark Registration for{" "}
                <b>{currencyFormatter(BASIC_PRICE)}</b>?
              </p>
              <p>As soon as your Trademark is approved - our legal team will obtain a copy for you!</p>
            </Text>
          </Modal.Body>
          <StyledFooter $spaceBetween>
            <Button
              type="button"
              variant="success"
              disabled={total <= 0}
              onClick={() => {
                setStep(STEPS.loading);
                createUpsell();
              }}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setRegisterDetails(null);
                setStep(STEPS.select);
              }}
            >
              {"No, I don't need a Certified Copy"}
            </Button>
          </StyledFooter>
        </Modal>
      );

    case STEPS.success:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: true })}>
          <Modal.Header closeButton>
            <Text>Certified Copy</Text>
          </Modal.Header>
          <Modal.Body>
            <Text>Thank you! Your Certified Copy purchase was successful!</Text>
          </Modal.Body>
          <StyledFooter>
            <Button variant="success" onClick={() => handleCloseModal({ update: true })}>
              OK
            </Button>
          </StyledFooter>
        </Modal>
      );

    case STEPS.error:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => handleCloseModal({ update: true })}>
          <Modal.Header closeButton>
            <Text>Certified Copy</Text>
          </Modal.Header>
          <Modal.Body>
            <ErrorBox>
              <div style={{ color: "red" }}>
                <b>Error:</b>
              </div>
              <div>{error}</div>
            </ErrorBox>
          </Modal.Body>
          <StyledFooter>
            <Button variant="success" onClick={() => handleCloseModal({ update: true })}>
              OK
            </Button>
          </StyledFooter>
        </Modal>
      );

    case STEPS.loading:
      return (
        <Modal centered show size="md" backdrop="static" onHide={() => {}}>
          <Modal.Header>
            <Text>Certified Copy</Text>
          </Modal.Header>
          <Modal.Body>
            <SpinnerBlock />
          </Modal.Body>
        </Modal>
      );

    default:
      return null;
  }
}

CertifiedCopyUpsell.propTypes = {
  upsellInfo: PropTypes.object,
  onCancel: PropTypes.func,
};

export default CertifiedCopyUpsell;
